import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const MenuSection = ({ menuItems, onAddToCart, onCustomOrder }) => {
  const [selectedCuisine, setSelectedCuisine] = useState('all');
  const [selectedDietary, setSelectedDietary] = useState([]);

  const cuisineTypes = ['all', 'Indian', 'Chinese', 'Italian', 'Thai', 'Mediterranean', 'Cake', 'Sweets'];
  const dietaryFilters = [
    { value: 'vegetarian', label: 'Vegetarian', icon: 'Leaf' },
    { value: 'vegan', label: 'Vegan', icon: 'Sprout' },
    { value: 'gluten-free', label: 'Gluten-Free', icon: 'Wheat' },
    { value: 'halal', label: 'Halal', icon: 'Moon' }
  ];

  const toggleDietaryFilter = (value) => {
    setSelectedDietary(prev =>
      prev?.includes(value) ? prev?.filter(v => v !== value) : [...prev, value]
    );
  };

  const filteredItems = menuItems?.filter(item => {
    const cuisineMatch = selectedCuisine === 'all' || item?.cuisine === selectedCuisine;
    const dietaryMatch = selectedDietary?.length === 0 || 
      selectedDietary?.every(diet => item?.dietary?.includes(diet));
    return cuisineMatch && dietaryMatch;
  });

  return (
    <div className="bg-card rounded-xl shadow-warm-md p-6 md:p-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h2 className="text-2xl md:text-3xl font-heading font-bold">Menu</h2>
        <Button
          variant="outline"
          iconName="Plus"
          iconPosition="left"
          onClick={onCustomOrder}
        >
          Request Custom Order
        </Button>
      </div>
      <div className="mb-6 space-y-4">
        <div>
          <label className="block text-sm font-caption font-semibold mb-3">Cuisine Type</label>
          <div className="flex flex-wrap gap-2">
            {cuisineTypes?.map((cuisine) => (
              <button
                key={cuisine}
                onClick={() => setSelectedCuisine(cuisine)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-smooth ${
                  selectedCuisine === cuisine
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted hover:bg-muted/80'
                }`}
              >
                {cuisine === 'all' ? 'All Cuisines' : cuisine}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-caption font-semibold mb-3">Dietary Preferences</label>
          <div className="flex flex-wrap gap-2">
            {dietaryFilters?.map((filter) => (
              <button
                key={filter?.value}
                onClick={() => toggleDietaryFilter(filter?.value)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-smooth ${
                  selectedDietary?.includes(filter?.value)
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted hover:bg-muted/80'
                }`}
              >
                <Icon name={filter?.icon} size={16} />
                {filter?.label}
              </button>
            ))}
          </div>
        </div>
      </div>
      {filteredItems?.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="Search" size={32} color="var(--color-muted-foreground)" />
          </div>
          <p className="text-muted-foreground">No items match your filters</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {filteredItems?.map((item) => (
            <div
              key={item?.id}
              className="bg-background rounded-lg overflow-hidden shadow-warm hover:shadow-warm-md transition-smooth"
            >
              <div className="relative h-48 overflow-hidden">
                <Image
                  src={item?.image}
                  alt={item?.imageAlt}
                  className="w-full h-full object-cover"
                />
                {item?.isNew && (
                  <div className="absolute top-3 left-3 px-3 py-1 bg-accent text-accent-foreground rounded-lg text-xs font-bold">
                    NEW
                  </div>
                )}
                {item?.isPopular && (
                  <div className="absolute top-3 right-3 px-3 py-1 bg-warning text-warning-foreground rounded-lg text-xs font-bold flex items-center gap-1">
                    <Icon name="TrendingUp" size={12} />
                    POPULAR
                  </div>
                )}
              </div>

              <div className="p-4">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <h3 className="font-heading font-semibold text-lg line-clamp-1">
                    {item?.name}
                  </h3>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Icon name="Star" size={16} color="var(--color-warning)" className="fill-current" />
                    <span className="text-sm font-semibold">{item?.rating}</span>
                  </div>
                </div>

                <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                  {item?.description}
                </p>

                <div className="flex flex-wrap gap-1.5 mb-3">
                  {item?.dietary?.map((diet, index) => (
                    <span
                      key={index}
                      className="px-2 py-0.5 bg-success/10 text-success rounded text-xs font-medium"
                    >
                      {diet}
                    </span>
                  ))}
                </div>

                <div className="flex items-center justify-between pt-3 border-t border-border">
                  <div>
                    <div className="text-2xl font-heading font-bold text-primary">
                      ${Number(item?.price_per_plate ?? item?.price ?? 0).toFixed(2)}
                    </div>
                    <div className="text-xs text-muted-foreground">per plate</div>
                  </div>
                  <Button
                    variant="default"
                    size="sm"
                    iconName="ShoppingCart"
                    iconPosition="left"
                    onClick={() => onAddToCart(item)}
                  >
                    Add
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MenuSection;